/**
 * タイムスロットの拡張表示に関するユーティリティ
 * 短い利用不可区間（5分・10分）を視認しやすくするための拡張ロジック
 */

type TimeSlot = {
  time: string;
  available: boolean;
  displayable: boolean;
};

/**
 * 利用不可スロットかどうかを判定する
 */
export const isUnavailableSlot = (slot: TimeSlot): boolean => {
  return !slot.available || !slot.displayable;
};

/**
 * 指定位置の後にある連続した空きスロットの数を取得する
 */
export const getAvailableStreakAfterLength = (
  timeSlots: TimeSlot[],
  startIndex: number,
): number => {
  let count = 0;
  for (let i = startIndex; i < timeSlots.length; i++) {
    if (!isUnavailableSlot(timeSlots[i])) {
      count++;
    } else {
      break;
    }
  }
  return count;
};

/**
 * 指定位置の前にある連続した空きスロットの数を取得する
 */
export const getAvailableStreakBeforeLength = (
  timeSlots: TimeSlot[],
  endIndex: number,
): number => {
  let count = 0;
  for (let i = endIndex - 1; i >= 0; i--) {
    if (!isUnavailableSlot(timeSlots[i])) {
      count++;
    } else {
      break;
    }
  }
  return count;
};

/** 利用不可区間の情報 */
type UnavailableRange = {
  startIndex: number;
  endIndex: number;
  length: number;
};

/**
 * 指定位置より前にある直近の利用不可区間を探す
 */
export const findPreviousUnavailableRange = (
  timeSlots: TimeSlot[],
  currentIndex: number,
): UnavailableRange | null => {
  let endIndex = -1;
  let startIndex = -1;

  for (let i = currentIndex - 1; i >= 0; i--) {
    if (isUnavailableSlot(timeSlots[i])) {
      if (endIndex === -1) {
        endIndex = i;
      }
      startIndex = i;
    } else if (endIndex !== -1) {
      break;
    }
  }

  if (startIndex === -1 || endIndex === -1) {
    return null;
  }

  return {
    startIndex,
    endIndex,
    length: endIndex - startIndex + 1,
  };
};

/**
 * 指定位置より後にある直近の利用不可区間を探す
 */
export const findNextUnavailableRange = (
  timeSlots: TimeSlot[],
  currentIndex: number,
): UnavailableRange | null => {
  let startIndex = -1;
  let endIndex = -1;

  for (let i = currentIndex + 1; i < timeSlots.length; i++) {
    if (isUnavailableSlot(timeSlots[i])) {
      if (startIndex === -1) {
        startIndex = i;
      }
      endIndex = i;
    } else if (startIndex !== -1) {
      break;
    }
  }

  if (startIndex === -1 || endIndex === -1) {
    return null;
  }

  return {
    startIndex,
    endIndex,
    length: endIndex - startIndex + 1,
  };
};

/** 拡張判定に必要な最小の連続空きスロット数 */
const MIN_AVAILABLE_STREAK_FOR_EXTENSION = 4;

/**
 * 空きスロットが利用不可区間の後方拡張として表示されるべきかを判定する
 * - 5分（1スロット）: 後方に1スロット拡張
 * - 10分（2スロット）: 後方に1スロット拡張
 */
export const shouldExtendAfterUnavailable = (
  timeSlots: TimeSlot[],
  currentIndex: number,
): boolean => {
  const prevRange = findPreviousUnavailableRange(timeSlots, currentIndex);
  if (!prevRange) return false;

  // 連続した利用不可が2スロット以下の場合のみ拡張
  if (prevRange.length > 2) return false;

  const distanceFromEnd = currentIndex - prevRange.endIndex - 1;
  // 後方に1スロットだけ拡張
  if (distanceFromEnd >= 1) return false;

  const availableAfter = getAvailableStreakAfterLength(
    timeSlots,
    prevRange.endIndex + 1,
  );

  return availableAfter >= MIN_AVAILABLE_STREAK_FOR_EXTENSION;
};

/**
 * 空きスロットが利用不可区間の前方拡張として表示されるべきかを判定する
 * - 5分（1スロット）の場合のみ: 前方に1スロット拡張
 */
export const shouldExtendBeforeUnavailable = (
  timeSlots: TimeSlot[],
  currentIndex: number,
): boolean => {
  const nextRange = findNextUnavailableRange(timeSlots, currentIndex);
  if (!nextRange) return false;

  // 5分（1スロット）の場合のみ前方拡張
  if (nextRange.length !== 1) return false;

  const distanceToStart = nextRange.startIndex - currentIndex - 1;
  // 前方に1スロットだけ拡張
  if (distanceToStart >= 1) return false;

  const availableBefore = getAvailableStreakBeforeLength(
    timeSlots,
    nextRange.startIndex,
  );

  return availableBefore >= MIN_AVAILABLE_STREAK_FOR_EXTENSION;
};

/**
 * 空きスロットが利用不可区間の拡張として表示されるべきかを判定する
 */
export const shouldShowAsExtendedUnavailable = (
  timeSlots: TimeSlot[],
  currentIndex: number,
): boolean => {
  return (
    shouldExtendAfterUnavailable(timeSlots, currentIndex) ||
    shouldExtendBeforeUnavailable(timeSlots, currentIndex)
  );
};

/** 選択期間の表示情報 */
export type SelectedDurationInfo = {
  /** 選択期間内かどうか */
  isInSelectedDuration: boolean;
  /** 拡張部分（グレー表示）かどうか */
  isExtendedPart: boolean;
};

/**
 * 指定スロットが選択期間内かどうか、また拡張部分かどうかを判定する
 * 5分・10分の選択時は15分幅に拡張して表示する（連続空きが十分ある場合のみ）
 */
export const getSelectedDurationInfo = (
  timeSlots: TimeSlot[],
  currentIndex: number,
  selectedStartTime: string,
  selectedDurationMinutes: number,
): SelectedDurationInfo => {
  const result: SelectedDurationInfo = {
    isInSelectedDuration: false,
    isExtendedPart: false,
  };

  if (!selectedStartTime || selectedDurationMinutes <= 0) {
    return result;
  }

  const startIndex = timeSlots.findIndex((s) => s.time === selectedStartTime);
  if (startIndex === -1) {
    return result;
  }

  const slotsCount = selectedDurationMinutes / 5; // 5分刻み

  // 5分と10分の場合は、幅が狭いため、15分と同等の幅（3スロット）を確保する
  // ただし、連続した空きが3スロット以下（15分以下）の場合は拡張しない
  const availableAfterStart = getAvailableStreakAfterLength(
    timeSlots,
    startIndex,
  );
  const shouldExtend =
    slotsCount < 3 && availableAfterStart >= MIN_AVAILABLE_STREAK_FOR_EXTENSION;
  const displaySlotsCount = shouldExtend ? 3 : slotsCount;
  const endIndex = startIndex + displaySlotsCount;

  if (currentIndex >= startIndex && currentIndex < endIndex) {
    result.isInSelectedDuration = true;
    // 拡張部分（実際の選択期間を超えた部分）
    if (shouldExtend && currentIndex >= startIndex + slotsCount) {
      result.isExtendedPart = true;
    }
  }

  return result;
};
